import React, { useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	TextField,
	Alert,
	Paper,
	IconButton,
	Tooltip,
	Link
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNickFetch } from '../hooks/useNickFetch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PsychologyIcon from '@mui/icons-material/Psychology';

type Props = {
	open: boolean;
	onClose: () => void;
	shop_id: number;
	hpb_salon_id?: string; // Hot PepperのサロンID
	onPromptGenerated?: (prompt: string) => void;
};

type Reference = {
	url: string;
	explanation: string;
};

export default function HotPepperPromptGeneratorDialog(props: Props) {
	const [generatedPrompt, setGeneratedPrompt] = useState<string>('');
	const [references, setReferences] = useState<Reference[]>([]);
	const [generatingPrompt, setGeneratingPrompt] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const { nickFetch } = useNickFetch();

	const handleGeneratePrompt = async () => {
		setGeneratingPrompt(true);
		setError('');

		try {
			const requestData: any = {
				shop_id: props.shop_id
			};

			const res = await nickFetch("/generate_blog_prompt", requestData);

			if (!res.error && res.data && res.data.prompt) {
				setGeneratedPrompt(res.data.prompt);
				setReferences(res.data.references || []);
			} else {
				setError('プロンプト生成に失敗しました');
			}
		} catch (error) {
			setError('プロンプト生成に失敗しました');
		} finally {
			setGeneratingPrompt(false);
		}
	};

	const handleCopy = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
		} catch (err) {
			// フォールバック: 古いブラウザ対応
			const textArea = document.createElement('textarea');
			textArea.value = text;
			document.body.appendChild(textArea);
			textArea.select();
			document.execCommand('copy');
			document.body.removeChild(textArea);
		}
	};

	const handleClose = () => {
		setGeneratedPrompt('');
		setReferences([]);
		setError('');
		setGeneratingPrompt(false);
		props.onClose();
	};

	return (
		<Dialog
			open={props.open}
			onClose={handleClose}
			fullWidth
			maxWidth="md"
			PaperProps={{
				sx: {
					maxHeight: '90vh'
				}
			}}
		>
			<DialogTitle>
				Hot PepperブログからAIプロンプト生成
			</DialogTitle>
			<DialogContent sx={{ overflowY: 'auto' }}>
				<Box py={2}>
					{/* 説明 */}
					<Box mb={3}>
						<Typography variant="body2" color="text.secondary" mb={1}>
							店舗のHot Pepperブログから口調・性格を分析して、AIプロンプトを自動生成します。
						</Typography>
						{props.hpb_salon_id && (
							<Typography variant="caption" color="text.secondary">
								使用するサロンID: <strong>{props.hpb_salon_id}</strong>
							</Typography>
						)}
						{!props.hpb_salon_id && (
							<Typography variant="caption" color="error">
								注意: Hot PepperのサロンIDが設定されていません。
							</Typography>
						)}
					</Box>

					{/* 生成ボタン */}
					<Box mb={3}>
						<LoadingButton
							variant="contained"
							size="large"
							startIcon={<PsychologyIcon />}
							onClick={handleGeneratePrompt}
							loading={generatingPrompt}
							disabled={generatingPrompt}
							fullWidth
						>
							Hot Pepperブログから生成
						</LoadingButton>
						{generatingPrompt && (
							<Box mt={2}>
								<Alert severity="info">
									<Typography variant="body2">
										プロンプトの生成には時間がかかります（30秒〜1分程度）<br />
										ブログ記事を分析中です。しばらくお待ちください...
									</Typography>
								</Alert>
							</Box>
						)}
					</Box>

					{/* エラー表示 */}
					{error && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{error}
						</Alert>
					)}

					{/* 生成結果 */}
					{generatedPrompt && (
						<Box>
							<Typography variant="h6" mb={2}>
								生成されたプロンプト
							</Typography>

							{/* 参考にした記事 */}
							{references.length > 0 && (
								<Box mb={3}>
									<Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
										参考にした記事
									</Typography>
									<Paper elevation={1} sx={{ p: 2 }}>
										{references.map((ref, index) => (
											<Box key={index} mb={index < references.length - 1 ? 2 : 0}>
												<Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
													{index + 1}.{' '}
													{ref.url ? (
														<Link
															href={ref.url}
															target="_blank"
															rel="noopener noreferrer"
															sx={{
																color: 'primary.main',
																textDecoration: 'none',
																'&:hover': {
																	textDecoration: 'underline'
																}
															}}
														>
															{ref.url}
														</Link>
													) : (
														'URL不明'
													)}
												</Typography>
												<Typography variant="body2" color="text.secondary" mb={1}>
													{ref.explanation || '参考にしました'}
												</Typography>
												{index < references.length - 1 && <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mt: 1 }} />}
											</Box>
										))}
									</Paper>
								</Box>
							)}

							<Paper elevation={1} sx={{ p: 2 }}>
								<Box display="flex" alignItems="center" mb={1}>
									<Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
										プロンプト
									</Typography>
									<Tooltip title="プロンプトをコピー">
										<IconButton
											onClick={() => handleCopy(generatedPrompt)}
											size="small"
										>
											<ContentCopyIcon />
										</IconButton>
									</Tooltip>
									<Button
										variant="outlined"
										size="small"
										onClick={handleGeneratePrompt}
										disabled={generatingPrompt}
										sx={{ ml: 1 }}
									>
										再生成
									</Button>
								</Box>
								<Box
									component="textarea"
									value={generatedPrompt}
									onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setGeneratedPrompt(e.target.value)}
									placeholder="生成されたプロンプトがここに表示されます"
									sx={{
										width: '100%',
										minHeight: '400px',
										padding: '16.5px 14px',
										fontFamily: 'monospace',
										fontSize: '0.875rem',
										lineHeight: '1.6',
										whiteSpace: 'pre-wrap',
										wordBreak: 'break-word',
										border: '1px solid rgba(0, 0, 0, 0.23)',
										borderRadius: '4px',
										resize: 'vertical',
										overflow: 'auto',
										backgroundColor: 'background.paper',
										color: 'text.primary',
										'&:focus': {
											outline: 'none',
											border: '2px solid #1976d2'
										},
										'&:hover': {
											borderColor: 'text.primary'
										},
										'&::placeholder': {
											color: 'text.secondary',
											opacity: 0.42
										}
									}}
								/>
								<Typography variant="caption" color="text.secondary" display="block" mt={1}>
									このプロンプトを編集することもできます。編集後、「このプロンプトを使用」ボタンでAIブログ生成に使用できます。
								</Typography>
							</Paper>
						</Box>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>
					閉じる
				</Button>
				{generatedPrompt && (
					<Button
						variant="contained"
						onClick={() => {
							if (props.onPromptGenerated) {
								props.onPromptGenerated(generatedPrompt);
							}
							handleClose();
						}}
					>
						このプロンプトを使用
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}

